import { defineMessages } from "react-intl";

// Everything both card layouts share: the two footers, the editor's buttons, and the message a
// settled row's status icon carries as both tooltip and aria-label. Nothing here is keyed on the
// action type — the four maps that are keyed on it live in ./actionTypeMessages, re-exported below
// so every existing import site keeps working.
const messages = defineMessages({
  applyFailed: {
    id: "chat.message.proposals.footer.applyFailed",
    defaultMessage: "Couldn't apply. Check your connection and try again.",
    description: "Card-level error line when the whole apply call failed",
  },
  // The wording the shared default used to carry. It moved here once a second type needed its own:
  // a deal that moved is the one thing this type's conflict is ever about, and it was the wrong
  // sentence for every other type that falls back to the shared string.
  changeDealStageConflictTooltip: {
    id: "chat.message.proposals.row.changeDealStage.conflictTooltip",
    defaultMessage: "The deal already moved, so this can't be applied",
    description:
      "Tooltip and aria-label on the red cross icon of a conflicted change_deal_stage row; it shares the icon with a rejected one, so this is what distinguishes them",
  },
  changeDealStageGone: {
    id: "chat.message.proposals.row.changeDealStage.gone",
    defaultMessage: "The stage this suggested no longer exists. Pick a new one to continue.",
    description: "Shown in a stage-change proposal's editor when the proposed stage was deleted",
  },
  changeDealStagePicker: {
    id: "chat.message.proposals.row.changeDealStage.picker",
    defaultMessage: "Choose a stage",
    description:
      "Button that opens the stage picker when the stage a proposal named no longer exists",
  },
  confirm: {
    id: "chat.message.proposals.footer.confirm",
    defaultMessage: "{count, plural, one {Confirm # item} other {Confirm # items}}",
    description: "Primary button on a proposal card; count = checked, still-pending proposals",
  },
  editActivityDetails: {
    id: "chat.message.proposals.row.logActivity.editActivityDetails",
    defaultMessage: "Edit details",
    description:
      "Used twice for the same edit surface: the link below a standalone log_activity card's field stack that opens the edit dialog, and that dialog's own title. The standalone shell has no corner pencil, unlike a bundled row",
  },
  editCancel: {
    id: "chat.message.proposals.row.editor.cancel",
    defaultMessage: "Cancel",
    description:
      "Cancel button in a proposal row's editor, whether that editor is inline or a dialog; it discards the edit rather than rejecting the proposal",
  },
  editDone: {
    id: "chat.message.proposals.row.editor.done",
    defaultMessage: "Done",
    description: "Done button in a proposal row's inline editor; commits the draft",
  },
  editLabel: {
    id: "chat.message.proposals.row.edit",
    defaultMessage: "Edit",
    description: "Accessible label of the pencil button that opens a proposal's inline editor",
  },
  editRecordDetails: {
    id: "chat.message.proposals.editRecordDetails",
    defaultMessage: "Edit details",
    description:
      "Link on a record_update proposal card that opens the modal for editing the proposed field values",
  },
  expired: {
    id: "chat.message.proposals.footer.expired",
    defaultMessage: "Expired",
    description: "Meta line when every proposal on the card lapsed before the rep confirmed",
  },
  expiresIn: {
    id: "chat.message.proposals.footer.expiresIn",
    defaultMessage: "Expires {relativeTime}",
    description:
      "Meta line under a proposal card; {relativeTime} is a localized relative time like 'in 54 minutes'",
  },
  fallbackBody: {
    id: "chat.message.proposals.row.unsupported.body",
    defaultMessage:
      "This suggestion can't be shown in this version of the app. You can cancel it instead.",
    description: "Body of the unsupported proposal row",
  },
  lossReasonValue: {
    id: "chat.message.proposals.row.changeDealStage.lossReasonValue",
    defaultMessage: "{label}: {name}",
    description:
      "The chosen loss reason on a stage-change proposal, shown with its label. {label} is the Deal field's own display name and {name} the reason. Kept as a message so locales that space punctuation differently — French puts a space before the colon — can set the separator.",
  },
  outcomeFailurePrefix: {
    id: "chat.message.proposals.outcome.failurePrefix",
    defaultMessage: "Couldn't apply: {reason}",
    description:
      "Wraps the platform's own failure reason, which arrives as a lowercase clause rather than a sentence — 'you can only change stops on your own Route'. Applied to every action type",
  },
  // Three of the six icons are shared by two statuses each, so the tooltip is the only thing that
  // tells them apart. It doubles as the icon's aria-label — what a screen reader reads now that no
  // worded badge is rendered.
  rowAppliedTooltip: {
    id: "chat.message.proposals.row.tooltip.applied",
    defaultMessage: "Applied",
    description: "Tooltip on the green check icon of a proposal the platform applied",
  },
  rowCancelledTooltip: {
    id: "chat.message.proposals.row.tooltip.cancelled",
    defaultMessage: "Rejected",
    description: "Tooltip on the red cross icon of a proposal the rep rejected",
  },
  // Names no entity, because it is what every action type falls back to — including one this build
  // cannot parse, which renders as the fallback row and still settles as a conflict. A type whose
  // conflict reads wrong in these words supplies its own through the registry's `conflictTooltip`.
  rowConflictTooltip: {
    id: "chat.message.proposals.row.tooltip.conflict",
    defaultMessage: "This record already changed, so this can't be applied",
    description:
      "Tooltip on the red cross icon of a proposal whose record moved on before the rep confirmed; it shares the icon with a rejected one, so this is what distinguishes them",
  },
  rowExpiredTooltip: {
    id: "chat.message.proposals.row.tooltip.expired",
    defaultMessage: "Expired",
    description: "Tooltip on the gray clock icon of a proposal whose deadline passed",
  },
  rowFailedTooltip: {
    id: "chat.message.proposals.row.tooltip.failed",
    defaultMessage: "Failed",
    description:
      "Tooltip on the red cross icon of a proposal the platform refused to apply; it shares the icon with a rejected one, so this is what distinguishes them",
  },
  rowStaleTooltip: {
    id: "chat.message.proposals.row.tooltip.stale",
    defaultMessage: "Already handled",
    description:
      "Tooltip on the gray clock icon of a proposal another submission settled first; it shares the icon with an expired one",
  },
  rowUnknownTooltip: {
    id: "chat.message.proposals.row.tooltip.unknown",
    defaultMessage: "Unknown",
    description:
      "Tooltip on the red cross icon of a proposal whose status this app version cannot place",
  },
  saveEdits: {
    id: "chat.message.proposals.row.logActivity.saveEdits",
    defaultMessage: "Save edits",
    description:
      "Primary button in the log_activity edit modal. Deliberately not 'Log activity': it only stores the rep's edits, and the card's Confirm is what applies them",
  },
  standaloneCancel: {
    id: "chat.message.proposals.standalone.cancel",
    defaultMessage: "Cancel",
    description:
      "Button on a single-proposal card that permanently rejects the proposed action. Not the same word as the editor's Cancel, which only discards an unsaved edit",
  },
  unknownStage: {
    id: "chat.message.proposals.row.changeDealStage.unknownStage",
    defaultMessage: "Unknown stage",
    description:
      "Stands in for a deal stage neither this app nor the server could name, on a stage-change proposal row",
  },
});

// The two reasons a proposal cannot be confirmed, shown as a tooltip on the disabled Confirm
// button or checkbox. Its own block, not loose keys, because a sibling imports it as a set.
export const blockedMessages = defineMessages({
  addRequiredFields: {
    id: "chat.message.proposals.blocked.addRequiredFields",
    defaultMessage: "Add the required fields first",
    description:
      "Tooltip on a proposal's disabled Confirm button or disabled checkbox when a field its layout marks required is still empty",
  },
  chooseRecordType: {
    id: "chat.message.proposals.blocked.chooseRecordType",
    defaultMessage: "Choose a record type first",
    description:
      "Tooltip on a create_record proposal's disabled Confirm button or disabled checkbox before the rep has picked Company, Person or Deal",
  },
  unsupportedAction: {
    id: "chat.message.proposals.blocked.unsupportedAction",
    defaultMessage: "This app version can't apply this one",
    description:
      "Tooltip on a proposal's disabled checkbox when the app cannot read the proposal — either an action type this version does not support, or a known type whose payload failed validation",
  },
});

// Bare "Open" (short) beside the standalone card's status text, and a per-type phrase everywhere
// else, including that same link's aria-label — read by the shared OpenCreatedRecordLink.
export const openCreatedRecordMessages = defineMessages({
  activity: {
    id: "chat.message.proposals.openCreatedRecord.activity",
    defaultMessage: "Open activity",
    description:
      "Label of the link that opens the record a create-capable proposal created, once applied, when that record is an Activity — both the bundled row's label and the standalone card's compact link's aria-label",
  },
  company: {
    id: "chat.message.proposals.openCreatedRecord.company",
    defaultMessage: "Open company",
    description:
      "Label of the link that opens the record a create-capable proposal created, once applied, when that record is a Company — both the bundled row's label and the standalone card's compact link's aria-label",
  },
  deal: {
    id: "chat.message.proposals.openCreatedRecord.deal",
    defaultMessage: "Open deal",
    description:
      "Label of the link that opens the record a create-capable proposal created, once applied, when that record is a Deal — both the bundled row's label and the standalone card's compact link's aria-label",
  },
  person: {
    id: "chat.message.proposals.openCreatedRecord.person",
    defaultMessage: "Open person",
    description:
      "Label of the link that opens the record a create-capable proposal created, once applied, when that record is a Person — both the bundled row's label and the standalone card's compact link's aria-label",
  },
  short: {
    id: "chat.message.proposals.openCreatedRecord.short",
    defaultMessage: "Open",
    description:
      "Visible text of the compact link beside a standalone card's settled status text, once applied; the per-type phrase moves into its aria-label instead",
  },
});

export {
  appliedProposalTitles,
  proposalTitles,
  relationshipTitles,
  scheduleEmailTitles,
  standaloneConfirmTitles,
  standaloneLabels,
} from "./actionTypeMessages";

export default messages;
